import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Phone, MapPin, MessageCircle } from "lucide-react";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState("");

  const validateEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val || val.trim() === "") {
      return "Email address is required";
    } else if (!emailRegex.test(val.trim())) {
      return "Please enter a valid email address (e.g. name@domain.com)";
    }
    return "";
  };

  const handleEmailChange = (val) => {
    setF((prev) => ({ ...prev, email: val }));
    if (emailTouched) {
      setEmailError(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(f.email));
  };

  const faq = [
    {
      q: "Is Hammam Mens Spa a gay spa in Delhi?",
      a: (
        <span>
          Yes, Hammam Mens Spa is a well-known and trusted <strong>gay spa in Delhi NCR</strong>, offering a safe, inclusive, and respectful environment for men.
        </span>
      ),
      border: "border-2 border-red-500/80 shadow-md shadow-red-950/30",
      iconColor: "text-red-400"
    },
    {
      q: "Do you provide male to male massage in Delhi?",
      a: (
        <span>
          Absolutely. We specialise in professional <strong>male to male massage</strong> services delivered by trained male therapists across all our Delhi NCR branches.
        </span>
      ),
      border: "border-2 border-emerald-400/80 shadow-md shadow-emerald-950/30",
      iconColor: "text-emerald-400"
    },
    {
      q: "Which locations do you operate in Delhi NCR?",
      a: (
        <span>
          We currently operate in <strong>Lajpat Nagar (South Delhi)</strong>, <strong>Rajouri Garden (West Delhi)</strong>, and <strong>Sikandarpur (Gurgaon)</strong>.
        </span>
      ),
      border: "border-2 border-amber-400/80 shadow-md shadow-amber-950/30",
      iconColor: "text-amber-400"
    },
    {
      q: "Is privacy maintained at Hammam Mens Spa?",
      a: "Yes. Privacy and discretion are our top priorities. All sessions are conducted in a professional and confidential manner.",
      border: "border-2 border-rose-400/80 shadow-md shadow-rose-950/30",
      iconColor: "text-rose-400"
    },
    {
      q: "What facilities are available at Hammam Mens Spa?",
      a: "Our facilities include steam bath, sauna, jacuzzi, luxury therapy rooms, body care treatments, and premium spa packages.",
      border: "border-2 border-cyan-400/80 shadow-md shadow-cyan-950/30",
      iconColor: "text-cyan-400"
    },
    {
      q: "How can I book a men's spa or male to male massage session?",
      a: "You can book your session by contacting your nearest Hammam Mens Spa branch directly or through our official website booking system.",
      border: "border-2 border-hgold/80 shadow-md shadow-yellow-950/30",
      iconColor: "text-hgold"
    },
    {
      q: "Do you offer at-location massage services?",
      a: (
        <span>
          Yes, Hammam Mens Spa also provides <strong>at-location massage services across India</strong>, subject to availability.
        </span>
      ),
      border: "border-2 border-purple-500/80 shadow-md shadow-purple-950/30",
      iconColor: "text-purple-400"
    }
  ];

  const submit = async (e) => {
    e.preventDefault();
    const err = validateEmail(f.email);
    setEmailTouched(true);
    if (err) {
      setEmailError(err);
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads/contact", f);
      toast.success("Message sent. We'll be in touch shortly.");
      setF({ name: "", email: "", phone: "", subject: "", message: "" });
      setEmailTouched(false);
      setEmailError("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden -mt-[72px] flex items-center">
        <img src="/hero_image_hammam.png" alt="Contact Hammam" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <motion.div
          initial={{ opacity: 0, y: -100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-20 w-full"
        >
          <div className="label-eyebrow mb-6 text-emerald-400 font-extrabold tracking-widest uppercase">Best Gay Spa in New Delhi</div>
          <h1 className="text-5xl lg:text-7xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-emerald-400 mb-6">
            Contact Us
          </h1>
          <p className="text-hsecondary text-lg max-w-2xl mt-4 leading-relaxed font-normal">
            We are here to assist you with your inquiries, feedback, or booking assistance.
          </p>
        </motion.div>
      </section>

      {/* Main Grid: Left Branch Card + Right Form Card */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
        {/* Left Side Outer Card with 3 Nested Branch Cards */}
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper border-2 border-hgold/70 shadow-xl shadow-yellow-950/20 flex flex-col justify-between space-y-6 h-full"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-2">Reach Us Anytime</div>
            <h2 className="text-3xl lg:text-4xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-yellow-400 font-bold mb-6">
              Our Branches
            </h2>

            <div className="space-y-5">
              {/* Branch 1: Lajpat Nagar */}
              <div className="p-5 rounded-xl border-2 border-red-500/80 bg-black/60 shadow-md shadow-red-950/20 hover:border-red-400 transition-all space-y-3">
                <h3 className="font-heading text-xl text-red-400 font-bold">Hammam Mens Spa | Lajpat Nagar</h3>
                <div className="space-y-2.5 text-sm text-white/90">
                  <a href="tel:+919560656913" className="flex items-center gap-3 hover:text-red-300 transition-colors">
                    <Phone size={16} className="text-red-400 shrink-0" />
                    <span>+91 9560656913</span>
                  </a>
                  <a href="https://wa.me/917065953969" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-red-300 transition-colors">
                    <MessageCircle size={16} className="text-red-400 shrink-0" />
                    <span>+91 7065953969 (WhatsApp)</span>
                  </a>
                  <a href="https://maps.app.goo.gl/65Dwm9Nt6GAhpuoL6" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-red-300 transition-colors font-medium">
                    <MapPin size={16} className="text-red-400 shrink-0" />
                    <span>Central Market, Lajpat Nagar II (Google Location)</span>
                  </a>
                </div>
              </div>

              {/* Branch 2: Rajouri Garden */}
              <div className="p-5 rounded-xl border-2 border-amber-400/80 bg-black/60 shadow-md shadow-amber-950/20 hover:border-amber-300 transition-all space-y-3">
                <h3 className="font-heading text-xl text-amber-400 font-bold">Hammam Mens Spa | Rajouri Garden</h3>
                <div className="space-y-2.5 text-sm text-white/90">
                  <a href="tel:+919560656913" className="flex items-center gap-3 hover:text-amber-300 transition-colors">
                    <Phone size={16} className="text-amber-400 shrink-0" />
                    <span>+91 9560656913</span>
                  </a>
                  <a href="https://wa.me/919560656913" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-amber-300 transition-colors">
                    <MessageCircle size={16} className="text-amber-400 shrink-0" />
                    <span>+91 9560656913 (WhatsApp)</span>
                  </a>
                  <a href="https://maps.app.goo.gl/5ChSfibhJxcBDTvH6" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-amber-300 transition-colors font-medium">
                    <MapPin size={16} className="text-amber-400 shrink-0" />
                    <span>Main Rajouri Garden Market (Google Location)</span>
                  </a>
                </div>
              </div>

              {/* Branch 3: Gurugram */}
              <div className="p-5 rounded-xl border-2 border-emerald-400/80 bg-black/60 shadow-md shadow-emerald-950/20 hover:border-emerald-300 transition-all space-y-3">
                <h3 className="font-heading text-xl text-emerald-400 font-bold">G69 Spa | Gurugram</h3>
                <div className="space-y-2.5 text-sm text-white/90">
                  <a href="tel:+917005120041" className="flex items-center gap-3 hover:text-emerald-300 transition-colors">
                    <Phone size={16} className="text-emerald-400 shrink-0" />
                    <span>+91 70051 20041</span>
                  </a>
                  <a href="https://wa.me/917005120041" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-emerald-300 transition-colors">
                    <MessageCircle size={16} className="text-emerald-400 shrink-0" />
                    <span>+91 70051 20041 (WhatsApp)</span>
                  </a>
                  <a href="https://maps.app.goo.gl/oT8hJtohHm2YajVy6" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-emerald-300 transition-colors font-medium">
                    <MapPin size={16} className="text-emerald-400 shrink-0" />
                    <span>Sikandarpur – Gurgaon (Google Location)</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Side Form Outer Card */}
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="h-full flex flex-col justify-between"
        >
          <form
            onSubmit={submit}
            className="p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper border-2 border-emerald-400/80 shadow-xl shadow-emerald-950/30 space-y-5 h-full flex flex-col justify-between"
            data-testid="contact-form"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1">Get In Touch</div>
              <h2 className="text-3xl font-heading text-white font-bold mb-4">Send Us a Message</h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Full Name</label>
                  <input
                    required
                    placeholder="Enter your full name"
                    data-testid="contact-name"
                    value={f.name}
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                    className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-white/90 block mb-1.5">Email Address</label>
                    <input
                      type="email"
                      required
                      placeholder="Enter email address"
                      data-testid="contact-email"
                      value={f.email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      onBlur={handleEmailBlur}
                      className={`w-full bg-black/70 border ${
                        emailError ? "border-red-500 focus:border-red-500" : "border-white/20 focus:border-emerald-400"
                      } px-4 py-3 text-sm text-white outline-none rounded-xl transition-all`}
                    />
                    {emailError && (
                      <span className="text-red-400 text-xs font-semibold mt-1.5 block animate-pulse">
                        ⚠️ {emailError}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-white/90 block mb-1.5">Phone Number</label>
                    <input
                      required
                      placeholder="Enter phone number"
                      data-testid="contact-phone"
                      value={f.phone}
                      onChange={(e) => setF({ ...f, phone: e.target.value })}
                      className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Subject</label>
                  <input
                    placeholder="Enter subject / inquiry topic"
                    data-testid="contact-subject"
                    value={f.subject}
                    onChange={(e) => setF({ ...f, subject: e.target.value })}
                    className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-white/90 block mb-1.5">Message</label>
                  <textarea
                    required
                    rows="4"
                    placeholder="Write your message here..."
                    data-testid="contact-message"
                    value={f.message}
                    onChange={(e) => setF({ ...f, message: e.target.value })}
                    className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all resize-none"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="gold-btn w-full justify-center rounded-xl py-3.5 font-extrabold text-xs uppercase tracking-wider mt-4 shadow-lg shadow-emerald-950/30"
            >
              {busy ? "Sending Message..." : "Send Message"}
            </button>
          </form>
        </motion.div>
      </div>

      {/* Divider Line */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-hgold/40 to-transparent my-4 max-w-7xl mx-auto" />

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
        <motion.div
          initial={{ opacity: 0, y: -100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="text-center mb-12"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400/90 font-extrabold mb-2">Got Questions?</div>
          <h2 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faq.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
              className={`rounded-2xl ${item.border} bg-gradient-to-b from-hpaper via-black/90 to-hpaper overflow-hidden transition-all duration-300`}
            >
              <button
                onClick={() => setActiveIdx(activeIdx === idx ? null : idx)}
                className="w-full text-left px-6 py-5 flex items-center justify-between transition-colors hover:bg-white/5"
              >
                <span className="font-heading text-white text-lg font-bold pr-4">{item.q}</span>
                <span
                  className={`${item.iconColor || "text-hgold"} text-2xl font-bold transform transition-transform duration-300 shrink-0`}
                  style={{ transform: activeIdx === idx ? "rotate(45deg)" : "rotate(0)" }}
                >
                  +
                </span>
              </button>
              <div
                className="transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: activeIdx === idx ? "500px" : "0px",
                  opacity: activeIdx === idx ? 1 : 0,
                  overflow: "hidden"
                }}
              >
                <div className="px-6 pb-6 text-white/80 text-sm leading-relaxed border-t border-white/10 pt-4 font-normal">
                  {item.a}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
